import { useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import {
  BoxSelect,
  Check,
  Crosshair,
  FileUp,
  FolderPlus,
  Globe2,
  MapPin,
  Move,
  PenLine,
  Plus,
  Quote,
  RotateCcw,
  Route,
  Sparkles,
  Square,
  Trash2,
  X,
} from "lucide-react";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";

import { api, ApiError, streamOpoProposals } from "../../api/client";
import type { Exercise, OpoCheck, OpoConfidence, OpoDraft, OpoDraftState, OpoSource, OpoTab, OpoUnit, Zone } from "../../api/types";
import { designateSoon } from "../../components/attention";
import { Callout } from "../../components/Callout";
import { Checkbox } from "../../components/Checkbox";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { Dialog } from "../../components/Dialog";
import { Spinner } from "../../components/EmptyState";
import { Motion, Presence } from "../../components/presence";
import { COMMAND_LABELS, COMMAND_RELATIONS } from "../../company";
import { Select } from "../../components/Select";
import { Badge, Button, IconButton, inputClass, inputSmClass, SectionLabel, Segmented, Tabs } from "../../components/ui";
import { genericEnemyLabel } from "../../enemyDisplay";
import { HelpButton } from "../../help/HelpButton";
import { unitDesignation } from "../../i18n/designation";
import { t, tServer } from "../../i18n/i18n";
import { missionName, unitMissionName } from "../../i18n/vocabulary";
import { seasonalLabel } from "../../weather";
import { VerificationMap } from "../../map/import/VerificationMap";
import { formatAreaKm2 } from "../../map/zones/format";
import { refreshTerrain, useZone } from "../../map/zones/useZones";
import { useFreeTerrainOffer } from "../../map/zones/zonesOpen";
import { zoneBusy } from "../../map/zones/zoneState";
import { ZoneProgressCard } from "../../views/manoeuvre/ZoneStep";
import {
  acceptProposal,
  addUnit,
  AXIS_SOURCE,
  BOUNDARY_KEEP,
  boundaryLabel,
  GRAPHIC_KEEP,
  GRAPHIC_KIND,
  INFERENCE_SOURCE,
  onFreeTerrain,
  pendingCheck,
  QUIET_NOTICES,
  terrainAction,
  terrainOf,
  terrainReady,
  terrainWaiting,
  TERRAIN_NOTE,
  TERRAIN_NOTICE,
  withBoundary,
  withGraphic,
  withTerrain,
  campCorridor,
  candidateLabel,
  checkAnchor,
  checkAt,
  checkLabel,
  checksByTab,
  chooseMatch,
  CONFIDENCE_LABEL,
  confirm,
  LEVELS,
  limaSlotLabel,
  MAP_TABS,
  matchValue,
  movedVertex,
  nextCheck,
  type Phase,
  PHASES,
  placeHow,
  placeLabel,
  pointed,
  rejectProposal,
  relationText,
  confirmable,
  ROLE_LABEL,
  roleLabels,
  TABS,
  unitCommand,
  unitsOf,
  unitStatusLabel,
  WARNING_LABEL,
  WARNING_TAB,
  weatherLabel,
  weatherOf,
  weatherOutcome,
  weatherUndecided,
  weatherView,
  withLima,
  withPhase,
  withPlace,
  withTask,
  withUnit,
  withWeatherTaken,
} from "./opoDraft";
import { Waiting } from "../../components/Waiting";

/** Écran de vérification d'un OPO sans données Generex (docs/20 §12, étape 2 de l'import) : la lecture du texte,
 *  rangée par onglets — émetteur et niveau, force et unités (retrouvées, à vérifier, à créer, ajoutées à la main ;
 *  rattachements et appuis lus), terrain (camp reconnu et ses indices), ennemi, LIMA et limites (carte où les limites
 *  et les LIMA se déplacent, calque joint), lieux (source, précision, homonymes situés, pointage d'un clic), phases et
 *  GDH (heure H), missions par unité et par temps (ce que la carte retient). Chaque valeur montre sa source et sa
 *  confiance ; un point à vérifier se confirme, une proposition de l'IA se valide ou s'écarte sur sa ligne, et le
 *  point suivant est désigné. Chaque correction est enregistrée aussitôt, et le serveur relit tout : l'import se
 *  rouvre tel quel. « Proposer avec l'IA » demande au modèle local les rubriques douteuses (flux interrompable,
 *  corrections suspendues pendant la lecture) ; « Créer le dossier » pulse quand il ne reste rien à vérifier. */

type Busy = "save" | "create" | "delete" | "overlay" | "terrain" | null;

function Confidence({ value, origin }: { value: OpoConfidence | null | undefined; origin?: string }) {
  if (origin === "llm") return <Badge tone="run" size="sm">{t("Proposé par l'IA")}</Badge>;
  if (!value) return null;
  const tone = value === "certain" ? "ok" : value === "doubtful" ? "warn" : "neutral";
  return (
    <Badge tone={tone} size="sm">
      {t(CONFIDENCE_LABEL[value])}
    </Badge>
  );
}

/** Extrait cité de l'ordre (paragraphe, ligne), et les autres mentions d'un même lieu. */
function SourcePanel({ sources }: { sources: OpoSource[] }) {
  return (
    <Motion origin="top" className="w-full basis-full">
      <div className="mt-1 space-y-1 rounded-md bg-canvas px-2 py-1.5 text-meta leading-snug break-words text-ink-soft">
        {sources.map((source, i) => (
          <p key={`${source.line}-${i}`}>
            <span className="font-medium text-ink-muted">
              {[source.paragraph && t("§ {n}", { n: source.paragraph }), source.line ? t("ligne {n}", { n: source.line }) : ""]
                .filter(Boolean)
                .join(" · ")}
            </span>
            {source.excerpt && <span className="mt-0.5 block italic">« {source.excerpt} »</span>}
          </p>
        ))}
      </div>
    </Motion>
  );
}

interface RowActions {
  check?: OpoCheck;
  onConfirm?: () => void;
  onAccept?: () => void;
  onReject?: () => void;
}

/** Commandes d'un point à vérifier sur sa ligne : une proposition de l'IA se valide ou s'écarte, un autre point
 *  non requis se confirme (« C'est juste »). */
function CheckActions({ check, onConfirm, onAccept, onReject }: RowActions) {
  if (!check) return null;
  if (check.kind === "llm" && onAccept && onReject) {
    return (
      <span className="flex items-center gap-1">
        <Button variant="secondary" size="sm" onClick={onAccept}>
          <Check size={14} /> {t("Valider")}
        </Button>
        <Button variant="ghost" size="sm" onClick={onReject}>
          <X size={14} /> {t("Écarter")}
        </Button>
      </span>
    );
  }
  if (!confirmable(check) || !onConfirm) return null;
  return (
    <Button variant="secondary" size="sm" onClick={onConfirm}>
      <Check size={14} /> {t("C'est juste")}
    </Button>
  );
}

/** Ligne d'une valeur lue : libellé, contrôle, confiance et source ; surlignée si elle est douteuse ou requise ; la
 *  source s'ouvre sous la ligne, sur toute sa largeur (rien ne déborde, même sur un téléphone). */
function VerifyRow({
  label,
  anchor,
  confidence,
  origin,
  source,
  sources,
  flagged,
  actions,
  children,
}: {
  label: ReactNode;
  anchor?: string;
  confidence?: OpoConfidence | null;
  origin?: string;
  source?: OpoSource | null;
  sources?: OpoSource[];
  flagged?: boolean;
  actions?: RowActions;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const cited = [source, ...(sources ?? [])].filter((s): s is OpoSource => Boolean(s && (s.excerpt || s.line)));
  return (
    <div
      data-check={anchor}
      className={clsx(
        "flex min-h-8 flex-wrap items-center gap-x-2 gap-y-1 border-b border-line px-2 py-1.5 last:border-b-0",
        (flagged || confidence === "doubtful" || actions?.check) && "rounded-md bg-warn-soft/60",
      )}
    >
      <span className="w-full min-w-0 shrink-0 text-ui break-words text-ink-muted sm:w-40">{label}</span>
      <div className="min-w-0 flex-1 basis-48">{children}</div>
      <span className="flex min-w-0 shrink-0 flex-wrap items-center gap-1">
        <Confidence value={confidence} origin={origin} />
        {actions && <CheckActions {...actions} />}
        {cited.length > 0 && (
          <IconButton
            size="xs"
            active={open}
            onClick={() => setOpen((v) => !v)}
            title={t("Voir la source dans l'ordre")}
            aria-label={t("Voir la source dans l'ordre")}
            aria-expanded={open}
          >
            <Quote size={12} />
          </IconButton>
        )}
      </span>
      <Presence show={open && cited.length > 0}>{open && <SourcePanel sources={cited} />}</Presence>
    </div>
  );
}

function Box({ children }: { children: ReactNode }) {
  return <div className="mt-1.5 rounded-lg border border-line">{children}</div>;
}

/** Champ de texte enregistré à la sortie du champ (Entrée ou perte du focus). */
function TextInput({ value, onCommit, small, ariaLabel, placeholder }: {
  value: string;
  onCommit: (value: string) => void;
  small?: boolean;
  ariaLabel: string;
  placeholder?: string;
}) {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  return (
    <input
      className={small ? inputSmClass : inputClass}
      value={text}
      aria-label={ariaLabel}
      placeholder={placeholder}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => text !== value && onCommit(text)}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
      }}
    />
  );
}

/** Avertissements de l'analyse qui concernent l'onglet affiché. */
function TabWarnings({ draft, tab, hide = [] }: { draft: OpoDraft; tab: OpoTab; hide?: string[] }) {
  const shown = draft.warnings.filter((w) => WARNING_LABEL[w] && WARNING_TAB[w] === tab && !hide.includes(w));
  return (
    <Presence show={shown.length > 0}>
      {shown.length > 0 && (
        <Callout tone="info">
          <ul className="list-disc space-y-0.5 pl-4">
            {shown.map((w) => (
              <li key={w}>{t(WARNING_LABEL[w]!)}</li>
            ))}
          </ul>
        </Callout>
      )}
    </Presence>
  );
}

/** Points de lecture d'un ordre d'état-major (matrice non lue, temps sans fenêtre…) et avis de l'onglet : chacun
 *  se confirme sur sa ligne (« C'est juste »). */
function TabChecks({ state, tab, actionsFor }: { state: OpoDraftState; tab: OpoTab; actionsFor: (anchor: string) => RowActions }) {
  const shown = state.context.checks.filter((c) => c.tab === tab && (c.kind === "structure" || c.kind === "notice"));
  if (shown.length === 0) return null;
  return (
    <section>
      <SectionLabel>{t("À vérifier")}</SectionLabel>
      <Box>
        {shown.map((c) => (
          <VerifyRow
            key={c.id}
            label={c.kind === "notice" ? t("Avis") : t("Lecture de l'ordre")}
            anchor={checkAnchor(c)}
            flagged={c.required}
            actions={{ ...actionsFor(checkAnchor(c)) }}
          >
            <p className="text-meta leading-snug text-ink-soft">{checkLabel(c, state.draft)}</p>
          </VerifyRow>
        ))}
      </Box>
    </section>
  );
}

// --- Onglets ---------------------------------------------------------------------------------------------------

interface TabProps {
  state: OpoDraftState;
  update: (next: OpoDraft) => void;
  /** Commandes du point à vérifier d'une ligne (ancre `data-check`). */
  actionsFor: (anchor: string) => RowActions;
}

function IssuerTab({ state, update, actionsFor }: TabProps) {
  const d = state.draft;
  const ctx = state.context;
  const cited = new Set(ctx.missions.map((m) => m.name));
  const proposal = actionsFor("issuer:issuer_proposal");
  return (
    <Motion origin="fade" className="space-y-4">
      <TabWarnings draft={d} tab="issuer" />
      <section>
        <SectionLabel>{t("Émetteur et niveau")}</SectionLabel>
        <Box>
          <VerifyRow
            label={t("Émetteur")}
            anchor="issuer:issuer"
            confidence={d.issuer.confidence}
            origin={d.issuer.origin}
            source={d.issuer.source}
            actions={actionsFor("issuer:issuer")}
          >
            <TextInput
              value={d.issuer.value ?? ""}
              ariaLabel={t("Émetteur")}
              onCommit={(value) => update({ ...d, issuer: { ...d.issuer, value, confidence: "certain", origin: "user" } })}
            />
            {d.issuer.proposal && d.issuer.proposal !== d.issuer.value && (
              <span className="mt-1 flex flex-wrap items-center gap-2" data-check="issuer:issuer_proposal">
                <Button
                  variant="link"
                  onClick={() => update({ ...d, issuer: { ...d.issuer, value: d.issuer.proposal, proposal: null, confidence: "certain", origin: "user" } })}
                >
                  {t("Reprendre la proposition de l'IA : {value}", { value: d.issuer.proposal })}
                </Button>
                {proposal.check && (
                  <Button variant="ghost" size="sm" onClick={() => update({ ...d, issuer: { ...d.issuer, proposal: null } })}>
                    <X size={14} /> {t("Écarter")}
                  </Button>
                )}
              </span>
            )}
          </VerifyRow>
          <VerifyRow label={t("Niveau de l'ordre")} anchor="issuer:level" flagged={!d.level}>
            <Select
              value={d.level ?? ""}
              aria-label={t("Niveau de l'ordre")}
              onChange={(e) => update({ ...d, level: (e.target.value || null) as OpoDraft["level"], level_chosen: true })}
            >
              <option value="">{t("À choisir")}</option>
              {LEVELS.map((l) => (
                <option key={l.value} value={l.value}>
                  {t(l.label)}
                </option>
              ))}
            </Select>
          </VerifyRow>
          <VerifyRow label={t("Mission du niveau")} anchor="issuer:mission" confidence={d.mission.confidence} flagged={!d.mission.value}>
            <Select
              value={d.mission.value ?? ""}
              aria-label={t("Mission du niveau")}
              onChange={(e) => update({ ...d, mission: { ...d.mission, value: e.target.value || null, confidence: "certain", origin: "user" } })}
            >
              <option value="">{t("À choisir")}</option>
              {[...cited].map((name) => (
                <option key={name} value={name}>
                  {missionName(name)}
                </option>
              ))}
            </Select>
          </VerifyRow>
          <VerifyRow label={t("Nom du dossier")}>
            <TextInput value={d.name} ariaLabel={t("Nom du dossier")} onCommit={(name) => update({ ...d, name: name.trim() || d.name })} />
          </VerifyRow>
          <VerifyRow label={t("Langue de l'ordre")}>
            <Select value={d.language} aria-label={t("Langue de l'ordre")} onChange={(e) => update({ ...d, language: e.target.value as "fr" | "en" })}>
              <option value="fr">{t("Français")}</option>
              <option value="en">{t("Anglais")}</option>
            </Select>
          </VerifyRow>
        </Box>
      </section>
      {(d.mission_text.value || d.intention.value) && (
        <section>
          <SectionLabel>{t("Lu dans l'ordre")}</SectionLabel>
          <Box>
            {d.mission_text.value && (
              <VerifyRow label={t("§ 2 Mission")} confidence={d.mission_text.confidence} source={d.mission_text.source}>
                <p className="text-ui leading-snug text-ink">{d.mission_text.value}</p>
              </VerifyRow>
            )}
            {d.intention.value && (
              <VerifyRow label={t("Intention du chef")} confidence={d.intention.confidence} source={d.intention.source}>
                <p className="text-ui leading-snug text-ink">{d.intention.value}</p>
              </VerifyRow>
            )}
          </Box>
        </section>
      )}
    </Motion>
  );
}

function UnitMatch({ unit, state, update }: Omit<TabProps, "actionsFor"> & { unit: OpoUnit }) {
  const d = state.draft;
  const targets = state.context.targets.filter((tg) => tg.side === unit.side);
  const creating = d.force.mode === "create";
  return (
    <Select value={matchValue(unit)} aria-label={t("Correspondance de {unit}", { unit: unitDesignation(unit.designation) })} onChange={(e) => update(chooseMatch(d, unit, e.target.value))}>
      {!creating &&
        targets.map((tg) => (
          <option key={tg.key} value={tg.key}>
            {tg.label}
          </option>
        ))}
      {creating && unit.target && <option value={unit.target}>{t("Créée dans l'ordre de bataille")}</option>}
      {!creating && !unit.target && unit.status !== "create" && unit.status !== "ignore" && <option value="">{t("À choisir")}</option>}
      {unit.side === "AMI" && (
        <option value="create">
          {d.level === "COMPAGNIE" && !creating ? t("Unité reçue") : t("À créer dans l'ordre de bataille")}
        </option>
      )}
      <option value="ignore">{t("Écarter")}</option>
    </Select>
  );
}

/** Rôle de l'unité : pour une force du catalogue ou construite, celui qu'elle tient sur la carte (gabarit), le rôle
 *  lu dans l'ordre étant proposé à part ; pour une force créée d'après l'ordre, le rôle lu, qui s'applique. */
function UnitRole({ unit, state, update }: Omit<TabProps, "actionsFor"> & { unit: OpoUnit }) {
  const d = state.draft;
  const creating = d.force.mode === "create";
  const onMap = state.context.roles?.[unit.id] ?? null;
  const value = creating || unit.role_chosen ? (unit.role ?? "") : (onMap ?? "");
  const read = !creating && !unit.role_chosen && unit.role && unit.role !== onMap ? unit.role : null;
  // Ordre d'un S/GTIA (docs/21 §4) : rôles des emplacements du S/GTIA (appui, pion de tête).
  const labels = roleLabels(d.level);
  return (
    <div className="min-w-0">
      <Select
        value={value}
        aria-label={t("Rôle de {unit}", { unit: unitDesignation(unit.designation) })}
        onChange={(e) => update(withUnit(d, unit.id, { role: e.target.value || null, role_chosen: true }))}
      >
        <option value="">{t("Rôle du dispositif")}</option>
        {Object.entries(labels).map(([key, label]) => (
          <option key={key} value={key}>
            {t(label)}
          </option>
        ))}
      </Select>
      {read && (
        <Button variant="link" className="mt-1" onClick={() => update(withUnit(d, unit.id, { role: read, role_chosen: true }))}>
          {t("Lu dans l'ordre : {role} — appliquer", { role: t(labels[read] ?? ROLE_LABEL[read] ?? read) })}
        </Button>
      )}
    </div>
  );
}

/** Unité reçue par le S/GTIA (niveau compagnie, docs/21, décision 5) : TACON ou OPCON, comme l'ordre l'écrit ou au
 *  choix de l'utilisateur ; définition publique de l'OTAN (AAP-6) sous le choix. */
function UnitCommand({ unit, state, update }: Omit<TabProps, "actionsFor"> & { unit: OpoUnit }) {
  const d = state.draft;
  const value = unitCommand(unit);
  return (
    <div className="space-y-1">
      <Select
        value={value}
        aria-label={t("Relation de commandement de {unit}", { unit: unitDesignation(unit.designation) })}
        onChange={(e) => update(withUnit(d, unit.id, { command: e.target.value as "TACON" | "OPCON", command_chosen: true }))}
      >
        {COMMAND_RELATIONS.map((relation) => (
          <option key={relation} value={relation}>
            {`${t("Reçue en renforcement")} · ${COMMAND_LABELS[relation].label}`}
          </option>
        ))}
      </Select>
      <p className="text-meta leading-snug text-ink-muted">
        {unit.command && !unit.command_chosen
          ? t("Lu dans l'ordre : {command}.", { command: unit.command })
          : !unit.command_chosen
            ? t("L'ordre ne le dit pas : TACON par défaut.")
            : t(COMMAND_LABELS[value].hint)}
      </p>
    </div>
  );
}

function UnitsTab({ state, update, actionsFor }: TabProps) {
  const d = state.draft;
  const ctx = state.context;
  const friends = unitsOf(d, "AMI");
  const forceValue = d.force.mode === "create" ? "create" : (d.force.ref ?? "");
  const [adding, setAdding] = useState("");
  const relations = d.relations ?? [];
  const relationStatus: Record<string, string> = {
    present: t("Dans l'ordre de bataille"),
    info: t("Pour information"),
  };
  return (
    <Motion origin="fade" className="space-y-4">
      <TabWarnings draft={d} tab="units" />
      <section>
        <SectionLabel>{t("Force commandée")}</SectionLabel>
        <Box>
          <VerifyRow label={t("Ordre de bataille")} anchor="units:force" flagged={d.force.mode !== "create" && !d.force.ref}>
            <Select
              value={forceValue}
              aria-label={t("Ordre de bataille")}
              onChange={(e) => {
                const value = e.target.value;
                const found = ctx.forces.find((f) => f.ref === value);
                update({
                  ...d,
                  force:
                    value === "create" || !found
                      ? { mode: "create", ref: null, label: d.issuer.value ?? "", chosen: true }
                      : { mode: found.user ? "built" : "catalogue", ref: found.ref, label: found.label, chosen: true },
                  units: d.units.map((u) => ({ ...u, chosen: false, status: u.status === "ignore" && u.chosen ? "ignore" : "check" })),
                });
              }}
            >
              {ctx.forces.map((f) => (
                <option key={f.ref} value={f.ref}>
                  {f.group ? `${f.label} · ${f.group}` : f.label}
                </option>
              ))}
              <option value="create">{t("Créer un ordre de bataille d'après l'ordre")}</option>
            </Select>
          </VerifyRow>
        </Box>
        <p className="mt-1.5 text-meta leading-snug text-ink-muted">
          {d.force.mode === "create"
            ? d.level === "COMPAGNIE"
              ? t("S/GTIA inconnu du catalogue : un ordre de bataille est créé dans votre licence, le S/GTIA sous son GTIA, avec les sections ou pelotons ci-dessous, face à l'ennemi générique.")
              : t("Force inconnue du catalogue : un ordre de bataille est créé dans votre licence avec les unités ci-dessous, face à l'ennemi générique.")
            : d.level === "COMPAGNIE"
            ? t("Une unité « reçue en renforcement » s'ajoute au dossier du S/GTIA, TACON ou OPCON, sans toucher à l'ordre de bataille.")
            : t("Une unité « à créer » s'ajoute à une copie de cet ordre de bataille, qui garde l'ennemi de cette force.")}
        </p>
      </section>
      <section>
        <SectionLabel>{t("Unités subordonnées")}</SectionLabel>
        <Box>
          {friends.length === 0 && (
            <p className="px-2 py-2 text-ui text-ink-muted" data-check="units:no_units">
              {t("Aucune unité reconnue dans l'ordre : ajoutez-les ci-dessous.")}
            </p>
          )}
          {friends.map((u) => {
            const anchor = `units:${u.id}`;
            const actions = actionsFor(anchor);
            return (
              <VerifyRow
                key={u.id}
                label={
                  <span className="flex flex-col">
                    <span className="font-medium text-ink">{unitDesignation(u.designation)}</span>
                    <span className="flex flex-wrap gap-1">
                      <Badge size="sm" tone={u.status === "certain" ? "ok" : u.status === "check" ? "warn" : u.status === "create" ? "accent" : "neutral"}>
                        {unitStatusLabel(d, u)}
                      </Badge>
                      {u.origin === "user" && !u.source && <Badge size="sm">{t("Ajoutée à la main")}</Badge>}
                    </span>
                  </span>
                }
                anchor={anchor}
                confidence={u.origin === "user" ? null : u.confidence}
                origin={u.origin}
                source={u.source}
                flagged={u.status === "check" && !u.chosen}
                actions={{
                  ...actions,
                  onAccept: () => update(acceptProposal(d, actions.check!)),
                  onReject: () => update(rejectProposal(d, actions.check!)),
                }}
              >
                <div className="grid gap-1.5 sm:grid-cols-2">
                  <UnitMatch unit={u} state={state} update={update} />
                  <UnitRole unit={u} state={state} update={update} />
                </div>
                <Checkbox
                  className="mt-1.5"
                  checked={u.effort}
                  onChange={(effort) => update(withUnit(d, u.id, { effort }))}
                  label={t("Effort")}
                  detail={u.sector ? t("Fuseau {sector}", { sector: u.sector }) : undefined}
                />
                {u.relation && <p className="mt-1 text-meta text-ink-muted">{t("Appui lu : {relation}", { relation: u.relation })}</p>}
                <Presence show={d.level === "COMPAGNIE" && d.force.mode !== "create" && u.status === "create"}>
                  <Motion origin="fade" className="mt-2">
                    <UnitCommand unit={u} state={state} update={update} />
                  </Motion>
                </Presence>
              </VerifyRow>
            );
          })}
          <div className="flex flex-wrap items-center gap-2 px-2 py-2.5">
            <div className="min-w-0 flex-1 basis-48">
              <input
                className={inputClass}
                value={adding}
                aria-label={t("Désignation de l'unité à ajouter")}
                placeholder={t("Désignation (« 2e RI », « A Coy »)")}
                onChange={(e) => setAdding(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && adding.trim()) {
                    update(addUnit(d, adding));
                    setAdding("");
                  }
                }}
              />
            </div>
            <Button
              variant="secondary"
              size="sm"
              disabled={!adding.trim()}
              onClick={() => {
                update(addUnit(d, adding));
                setAdding("");
              }}
              data-attention="opo-add-unit"
            >
              <Plus size={14} /> {t("Ajouter une unité")}
            </Button>
          </div>
        </Box>
        <p className="mt-1.5 text-meta leading-snug text-ink-muted">
          {t("Une unité ajoutée est rapprochée de l'ordre de bataille, ou créée avec lui ; ses missions se choisissent dans « Missions par temps ».")}
        </p>
      </section>
      {relations.length > 0 && (
        <section>
          <SectionLabel>{t("Rattachements et appuis lus")}</SectionLabel>
          <Box>
            {relations.map((r) => {
              const anchor = `units:${r.id}`;
              return (
                <VerifyRow
                  key={r.id}
                  label={<span className="font-medium text-ink">{r.unit}</span>}
                  anchor={anchor}
                  source={r.source}
                  actions={{ ...actionsFor(anchor) }}
                >
                  <p className="text-ui leading-snug text-ink">{relationText(r)}</p>
                  <p className="mt-0.5 text-meta text-ink-muted">{relationStatus[ctx.relations?.[r.id] ?? "info"] ?? relationStatus.info}</p>
                </VerifyRow>
              );
            })}
          </Box>
        </section>
      )}
    </Motion>
  );
}

/** Commandes du terrain libre passées à l'onglet « Terrain » (et au reste de la fenêtre). */
interface TerrainProps {
  /** Mode de la carte propre au terrain : repère à pointer, axe à tracer, emprise à ajuster. */
  mapMode: TerrainMapMode;
  setMapMode: (mode: TerrainMapMode) => void;
  /** Action sur le terrain libre de l'import (`POST …/terrain`). */
  onAction: (action: "prepare" | "reuse" | "extend" | "retry" | "release", extra?: { zone_id?: string }) => void;
  acting: boolean;
  /** État relevé du terrain lié pendant sa préparation (sinon celui de la vérification). */
  zone: Zone | null;
  onIssuer: () => void;
  /** Action attendue désignée dans l'onglet (une seule dans la fenêtre). */
  designated: boolean;
  /** Préparation refusée parce qu'une autre se prépare déjà pour la licence : « Préparer le terrain » ne pulse plus. */
  prepareRefused: boolean;
}

type TerrainMapMode = "anchor" | "axis" | "adjust" | null;

/** Repère des coordonnées écrites sans zone de grille : zone déduite, ce qui l'a donnée et son doute ; « C'est juste »
 *  tant qu'elle est à confirmer (douteuse ou lue dans le texte, même avec un terrain lié), « Pointer un repère ». */
function GridRow({ state, update, actionsFor, mapMode, setMapMode, designated }: TabProps & TerrainProps) {
  const d = state.draft;
  const terrain = state.context.terrain;
  const inference = terrain?.inference;
  const choice = terrainOf(d);
  if (!inference || (!inference.short && !choice.anchor)) return null;
  const actions = actionsFor("terrain:grid_zone");
  const linked = Boolean(terrain?.zone);
  // Point requis tant que la zone n'est pas confirmée ; sans point, plus de doute à lever.
  const toConfirm = Boolean(actions.check);
  const doubtful = inference.doubtful && toConfirm;
  const confidence = !inference.zone ? null : doubtful ? "doubtful" : toConfirm ? "probable" : "certain";
  // La note du serveur redit la source quand la zone vient des carrés seuls.
  const note = inference.note && inference.source !== "prior" ? tServer(inference.note) : null;
  return (
    <section>
      <SectionLabel>{t("Coordonnées écrites sans zone de grille")}</SectionLabel>
      <Box>
        <VerifyRow label={t("Zone de grille")} anchor="terrain:grid_zone" confidence={confidence} flagged={!inference.zone}>
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-ui text-ink tabular-nums">{inference.zone ?? t("Inconnue")}</span>
            {inference.source && <span className="text-meta text-ink-muted">{t(INFERENCE_SOURCE[inference.source] ?? inference.source)}</span>}
          </div>
          <p className="mt-0.5 text-meta leading-snug text-ink-muted">
            {t("{n} coordonnée(s) sans zone, {m} placée(s)", { n: inference.short, m: inference.resolved })}
            {note && ` · ${note}`}
          </p>
          {(!linked || toConfirm) && (
            <div className="mt-1.5 flex flex-wrap items-center gap-2">
              {toConfirm && (
                <Button variant="secondary" size="sm" highlight={designated} onClick={actions.onConfirm} data-attention="opo-grid-confirm">
                  <Check size={14} /> {t("C'est juste")}
                </Button>
              )}
              <Button
                variant={mapMode === "anchor" ? "primary" : "secondary"}
                size="sm"
                onClick={() => setMapMode(mapMode === "anchor" ? null : "anchor")}
              >
                <Crosshair size={14} />{" "}
                {mapMode === "anchor" ? t("Cliquez sur la carte") : choice.anchor ? t("Pointer un autre repère") : t("Pointer un repère")}
              </Button>
              {choice.anchor && (
                <Button variant="ghost" size="sm" onClick={() => update(withTerrain(d, { anchor: null }))}>
                  <X size={14} /> {t("Retirer le repère")}
                </Button>
              )}
            </div>
          )}
        </VerifyRow>
      </Box>
      <Presence show={toConfirm}>
        {toConfirm && (
          <Motion origin="fade">
            <p className="mt-1.5 text-meta leading-snug text-ink-muted">
              {inference.source === "mention"
                ? t("Zone lue dans le texte de l'ordre : vérifiez-la avant de préparer le terrain, ou pointez un repère près du terrain de l'ordre.")
                : t("Les lettres des carrés de 100 km se répètent d'une zone à l'autre : un repère pointé près du terrain de l'ordre lève le doute.")}
            </p>
          </Motion>
        )}
      </Presence>
    </section>
  );
}

/** Terrain libre de l'import : emprise proposée (ajustable), préparation, terrain prêt, échec, extension. */
function FreeTerrain(props: TabProps & TerrainProps) {
  const { state, update, actionsFor, mapMode, setMapMode, onAction, acting, zone, onIssuer, designated, prepareRefused } = props;
  const d = state.draft;
  const terrain = state.context.terrain!;
  const choice = terrainOf(d);
  const proposal = terrain.proposal;
  const linked = terrain.zone;
  const expected = terrainAction(terrain);
  const shown = zone ?? (linked as Zone | null);
  const busy = terrain.state === "queued" || terrain.state === "preparing" || terrain.state === "extending";
  // Terrain dont l'import a lancé la préparation : le retirer pendant qu'il se prépare l'annule (place de la licence
  // libérée) ; un terrain repris de la licence est seulement délié.
  const cancels = busy && Boolean(terrain.own);
  const release = (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => onAction("release")}
      disabled={acting}
      title={cancels ? t("La préparation lancée par cet import est annulée.") : t("Le terrain reste dans vos terrains libres.")}
    >
      <X size={14} /> {t("Retirer ce terrain")}
    </Button>
  );
  const extendCheck = actionsFor("terrain:extend");
  const drawButtons = (
    <>
      <Button
        variant={mapMode === "axis" ? "primary" : "secondary"}
        size="sm"
        highlight={designated && expected === "area" && mapMode === null}
        onClick={() => setMapMode(mapMode === "axis" ? null : "axis")}
        data-attention="opo-terrain-axis"
      >
        <Route size={14} /> {mapMode === "axis" ? t("Cliquez l'arrière, puis l'avant") : t("Tracer l'axe")}
      </Button>
      {!terrain.inference?.short && (
        <Button variant={mapMode === "anchor" ? "primary" : "secondary"} size="sm" onClick={() => setMapMode(mapMode === "anchor" ? null : "anchor")}>
          <Crosshair size={14} /> {mapMode === "anchor" ? t("Cliquez sur la carte") : t("Pointer un repère")}
        </Button>
      )}
    </>
  );
  const proposedShown = Boolean(proposal) && !linked && (terrain.state === "proposed" || terrain.state === "confirm_grid");
  const reuseShown = !linked && terrain.reuse.length > 0;
  return (
    <section>
      <SectionLabel>{t("Terrain libre")}</SectionLabel>
      <Presence show={terrain.state === "needs_level"}>
        {terrain.state === "needs_level" && (
          <Motion origin="fade" className="mt-1.5" data-check="terrain:zone">
            <Callout
              tone="warn"
              title={t("Niveau de l'ordre à choisir")}
              action={
                <Button variant="link" highlight={designated} onClick={onIssuer}>
                  {t("Onglet Émetteur et niveau")}
                </Button>
              }
            >
              {t("La largeur et la marge du terrain dépendent du niveau de l'ordre : choisissez-le d'abord.")}
            </Callout>
          </Motion>
        )}
      </Presence>
      <Presence show={terrain.state === "needs_area"}>
        {terrain.state === "needs_area" && (
          <Motion origin="fade" className="mt-1.5" data-check="terrain:zone_area">
            <Callout tone="warn" title={t("Rien ne situe l'ordre sur la carte")}>
              {t("Aucune coordonnée ni aucun lieu de l'ordre ne situe la manœuvre : tracez son axe sur la carte (arrière, puis avant côté ennemi), ou pointez un repère.")}
            </Callout>
            {!proposal && <div className="mt-2 flex flex-wrap items-center gap-2">{drawButtons}</div>}
          </Motion>
        )}
      </Presence>
      <Presence show={proposedShown}>
        {proposedShown && proposal && (
          <Motion origin="fade">
            <Box>
              <VerifyRow label={t("Emprise proposée")} anchor="terrain:zone" flagged={Boolean(proposal.error)}>
                <p className="text-ui text-ink tabular-nums">
                  {proposal.footprint
                    ? `${formatAreaKm2(proposal.footprint.area_km2)} · ${t(LEVELS.find((l) => l.value === proposal.level)?.label ?? proposal.level)}`
                    : tServer(proposal.error ?? "")}
                </p>
                <p className="mt-0.5 text-meta leading-snug text-ink-muted">
                  {t("Axe {source}", { source: t(AXIS_SOURCE[proposal.source] ?? proposal.source) })}
                  {proposal.points > 0 && ` · ${t("{n} lieu(x) de l'ordre pris en compte", { n: proposal.points })}`}
                  {proposal.dropped > 0 && ` · ${t("{n} lieu(x) isolé(s) écarté(s)", { n: proposal.dropped })}`}
                </p>
                {proposal.notes.filter((n) => TERRAIN_NOTE[n]).map((n) => (
                  <p key={n} className="mt-0.5 text-meta leading-snug text-warn">
                    {t(TERRAIN_NOTE[n]!)}
                  </p>
                ))}
              </VerifyRow>
            </Box>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                highlight={designated && expected === "prepare" && mapMode === null && !prepareRefused}
                blocked={terrain.state !== "proposed" || Boolean(proposal.error) || !terrain.open}
                onBlocked={() =>
                  designateSoon(`[data-check="terrain:${terrain.state === "confirm_grid" ? "grid_zone" : "zone"}"]`, "ring", 0)
                }
                disabled={acting}
                onClick={() => onAction("prepare")}
                data-attention="opo-terrain-prepare"
              >
                {acting ? <Spinner decorative /> : <Globe2 size={14} />} {t("Préparer le terrain")}
              </Button>
              <Button
                variant={mapMode === "adjust" ? "primary" : "secondary"}
                size="sm"
                onClick={() => setMapMode(mapMode === "adjust" ? null : "adjust")}
              >
                <Move size={14} /> {mapMode === "adjust" ? t("Terminer l'ajustement") : t("Ajuster l'emprise")}
              </Button>
              {drawButtons}
              {choice.axis && (
                <Button variant="ghost" size="sm" onClick={() => update(withTerrain(d, { axis: null }))}>
                  <RotateCcw size={14} /> {t("Reprendre l'emprise proposée")}
                </Button>
              )}
            </div>
            <Presence show={mapMode === "adjust"}>
              {mapMode === "adjust" && (
                <Motion origin="top">
                  <p className="mt-1.5 text-meta leading-snug text-ink-muted">
                    {t("Glissez les poignées bleues : l'arrière, l'avant ou le milieu de l'emprise. La largeur suit la doctrine du niveau.")}
                  </p>
                </Motion>
              )}
            </Presence>
            {!terrain.open && (
              <p className="mt-1.5 text-meta leading-snug text-warn">{t("Le terrain libre n'est pas ouvert sur ce serveur : choisissez un camp.")}</p>
            )}
            <p className="mt-1.5 text-meta leading-snug text-ink-muted">
              {t("Seule l'emprise du terrain est envoyée au service de données cartographiques, jamais un nom de lieu de l'ordre. La vérification continue pendant la préparation.")}
            </p>
          </Motion>
        )}
      </Presence>
      <Presence show={reuseShown}>
        {reuseShown && (
          <Motion origin="fade" className="mt-3">
            <SectionLabel>{t("Terrains de votre licence qui couvrent l'ordre")}</SectionLabel>
            <Box>
              {terrain.reuse.map((z) => (
                <VerifyRow key={z.id} label={<span className="font-medium text-ink">{z.name}</span>}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-meta text-ink-muted tabular-nums">
                      {[z.area_km2 ? formatAreaKm2(z.area_km2) : null, z.status === "ready" ? t("Prêt") : t("En préparation")]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                    <Button variant="secondary" size="sm" onClick={() => onAction("reuse", { zone_id: z.id })} disabled={acting}>
                      <Check size={14} /> {t("Employer ce terrain")}
                    </Button>
                  </div>
                </VerifyRow>
              ))}
            </Box>
          </Motion>
        )}
      </Presence>
      <Presence show={Boolean(linked && shown && busy)}>
        {linked && shown && busy && (
          <Motion origin="fade" className="mt-1.5 space-y-2" data-check={terrain.state === "extending" ? "terrain:extend" : "terrain:zone"}>
            <ZoneProgressCard
              title={
                terrain.state === "extending"
                  ? t("Extension du terrain « {name} »", { name: linked.name })
                  : t("Préparation du terrain « {name} »", { name: linked.name })
              }
              zone={shown}
              target="opo-terrain-progress"
              onCancel={cancels ? () => onAction("release") : undefined}
              cancelling={acting}
            />
            <Callout tone="info">
              {terrain.state === "extending"
                ? t("Vous pouvez continuer la vérification : la création du dossier attend la fin de l'extension. Dès qu'elle est finie, les lieux qui débordaient sont résolus sur le terrain étendu.")
                : t("Vous pouvez continuer la vérification, ou la reprendre plus tard : la préparation continue sur le serveur. Dès que le terrain est prêt, les lieux de l'ordre sont résolus sur ses données et la carte se peuple.")}
            </Callout>
            {!cancels && <div className="flex flex-wrap gap-2">{release}</div>}
          </Motion>
        )}
      </Presence>
      <Presence show={Boolean(linked) && terrain.state === "failed"}>
        {linked && terrain.state === "failed" && (
          <Motion origin="fade" className="mt-1.5 space-y-2" data-check="terrain:zone">
            <Callout tone="danger" title={t("La préparation du terrain n'a pas abouti")}>
              {shown?.error_message ? tServer(shown.error_message) : t("Relancez la préparation ; si elle échoue de nouveau, retirez ce terrain et ajustez l'emprise.")}
            </Callout>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" highlight={designated} onClick={() => onAction("retry")} disabled={acting} data-attention="opo-terrain-retry">
                <RotateCcw size={14} /> {t("Relancer la préparation")}
              </Button>
              {release}
            </div>
          </Motion>
        )}
      </Presence>
      <Presence show={Boolean(linked) && (terrain.state === "ready" || terrain.state === "extend")}>
        {linked && (terrain.state === "ready" || terrain.state === "extend") && (
          <Motion origin="fade" className="mt-1.5 space-y-2">
            <Box>
              <VerifyRow label={t("Terrain de l'ordre")} confidence="certain">
                <p className="text-ui font-medium text-ink">{linked.name}</p>
                <p className="mt-0.5 text-meta text-ink-muted tabular-nums">
                  {[linked.area_km2 ? formatAreaKm2(linked.area_km2) : null, linked.climate_label ? tServer(linked.climate_label) : null]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
                {terrain.reused && <p className="mt-0.5 text-meta text-ink-muted">{t("Terrain de votre licence repris : aucune préparation n'a été nécessaire.")}</p>}
              </VerifyRow>
              {terrain.state === "extend" && (
                <VerifyRow label={t("Avis")} anchor="terrain:extend" flagged={Boolean(extendCheck.check)} actions={{ ...extendCheck }}>
                  <p className="text-meta leading-snug text-ink-soft">
                    {extendCheck.check
                      ? t("Le fuseau ou des lieux de l'ordre débordent du terrain préparé : étendez-le pour les couvrir, ou confirmez pour garder le terrain tel quel.")
                      : t("Terrain gardé tel quel : le fuseau ou des lieux de l'ordre débordent du terrain préparé. Vous pouvez encore l'étendre.")}
                  </p>
                  <Button
                    className="mt-1.5"
                    variant={extendCheck.check ? "primary" : "secondary"}
                    size="sm"
                    highlight={designated && Boolean(extendCheck.check)}
                    onClick={() => onAction("extend")}
                    disabled={acting}
                    data-attention="opo-terrain-extend"
                  >
                    <BoxSelect size={14} /> {t("Étendre le terrain")}
                  </Button>
                </VerifyRow>
              )}
            </Box>
            <div className="flex flex-wrap gap-2">{release}</div>
          </Motion>
        )}
      </Presence>
    </section>
  );
}

function TerrainTab(props: TabProps & TerrainProps) {
  const { state, update } = props;
  const d = state.draft;
  const ctx = state.context;
  const terrain = ctx.terrain;
  const zoneMode = terrain?.mode === "zone";
  const offer = useFreeTerrainOffer(false);
  const { data: maps = [] } = useQuery({ queryKey: ["maps"], queryFn: api.maps, staleTime: 30_000 });
  const camps = maps.filter((m) => (m.kind ?? "camp") === "camp");
  const detected = ctx.camps.find((c) => c.slug === d.camp);
  // Terrain libre proposé quand le serveur l'a ouvert et que la carte mondiale est là ; un ordre déjà sur un terrain
  // libre le garde affiché (fermé ensuite, il le dit).
  const freeOffered = zoneMode || (offer.offered && (terrain?.open ?? true));
  const site = zoneMode ? "zone" : "camp";
  // « Ordre hors des camps » : dit tant que rien n'est lié (ensuite, l'état du terrain le dit).
  const outsideShown = Boolean(ctx.outside && zoneMode && !terrainOf(d).chosen && !terrain?.zone);
  const evidence = !zoneMode && detected && detected.evidence.length > 0 ? detected : null;
  return (
    <Motion origin="fade" className="space-y-4">
      <Presence show={outsideShown}>
        {outsideShown && (
          <Callout tone="info" title={t("Ordre hors des camps")}>
            {t("Les lieux de cet ordre ne sont dans aucun camp connu : son terrain libre est tiré de l'ordre (coordonnées, lieux et fuseau), partout sur le globe.")}
          </Callout>
        )}
      </Presence>
      <Presence show={ctx.outside && !zoneMode && !freeOffered && offer.known}>
        {ctx.outside && !zoneMode && !freeOffered && offer.known && (
          <Callout tone="info" title={t("Ordre hors des camps")}>
            {offer.reason === "no-world-map"
              ? t("Les lieux de cet ordre ne sont dans aucun camp connu, et le terrain libre demande la carte mondiale, absente de ce serveur : choisissez un camp pour continuer.")
              : t("Les lieux de cet ordre ne sont dans aucun camp connu, et le terrain libre n'est pas ouvert sur ce serveur : choisissez un camp pour continuer.")}
          </Callout>
        )}
      </Presence>
      <Presence show={Boolean(ctx.manual_camp)}>
        {ctx.manual_camp && (
          <Callout tone="info" title={t("Camp choisi à la main")}>
            {t("Les lieux de l'ordre ne sont pas dans ce camp : pointez-les sur la carte (onglet « Lieux »).")}
          </Callout>
        )}
      </Presence>
      {freeOffered && (
        <section>
          <SectionLabel>{t("Lieu de l'exercice")}</SectionLabel>
          <Segmented
            className="mt-1.5"
            ariaLabel={t("Lieu de l'exercice")}
            options={[
              { value: "camp", label: t("Espace d'entraînement") },
              { value: "zone", label: t("Terrain libre") },
            ]}
            value={site}
            onChange={(mode) => update(withTerrain(mode === "camp" ? { ...d } : { ...d, camp: null, camp_chosen: false }, { mode, chosen: true }))}
          />
        </section>
      )}
      <Presence show={!zoneMode}>
        {!zoneMode && (
          <Motion origin="fade">
            <section>
              <SectionLabel>{t("Camp")}</SectionLabel>
              <Box>
                <VerifyRow label={t("Camp de l'ordre")} anchor="terrain:camp" flagged={!d.camp} confidence={detected?.matched ? "probable" : d.camp ? "certain" : null}>
                  <Select value={d.camp ?? ""} aria-label={t("Camp de l'ordre")} onChange={(e) => update({ ...d, camp: e.target.value || null, camp_chosen: true })}>
                    <option value="">{t("À choisir")}</option>
                    {camps.map((m) => (
                      <option key={m.slug} value={m.slug}>
                        {m.name}
                      </option>
                    ))}
                  </Select>
                </VerifyRow>
              </Box>
            </section>
          </Motion>
        )}
      </Presence>
      <Presence show={Boolean(evidence)}>
        {evidence && (
          <Motion origin="fade">
            <section>
              <SectionLabel>{t("Indices trouvés dans l'ordre")}</SectionLabel>
              <Box>
                {evidence.evidence.map((e, i) => (
                  <VerifyRow key={`${e.kind}-${i}`} label={e.kind === "camp" ? t("Nom du camp") : e.kind === "place" ? t("Lieu du camp") : t("Coordonnée")}>
                    <span className="text-ui text-ink">{e.text}</span>
                    {e.shared && <span className="ml-2 text-meta text-ink-muted">{t("aussi dans un autre camp")}</span>}
                  </VerifyRow>
                ))}
              </Box>
            </section>
          </Motion>
        )}
      </Presence>
      <Presence show={Boolean(zoneMode && terrain)}>
        {zoneMode && terrain && (
          // Terrain lié (en préparation, prêt) : son état d'abord ; sinon la zone de grille, qui conditionne l'emprise.
          <Motion origin="fade" className={clsx("flex flex-col gap-4", terrain.zone && "flex-col-reverse")}>
            <GridRow {...props} designated={props.designated && terrainAction(terrain) === "confirm"} />
            <FreeTerrain {...props} />
          </Motion>
        )}
      </Presence>
    </Motion>
  );
}

function EnemyTab({ state, update, actionsFor }: TabProps) {
  const d = state.draft;
  const enemies = unitsOf(d, "ENI");
  const places = new Map(d.places.map((p) => [p.id, p]));
  return (
    <Motion origin="fade" className="space-y-4">
      <section>
        <SectionLabel>{t("Ennemi")}</SectionLabel>
        <Box>
          <VerifyRow label={t("Ennemi du dossier")}>{state.context.french && d.enemy_type ? d.enemy_type : genericEnemyLabel()}</VerifyRow>
          {d.enemy_modes.map((m) => (
            <VerifyRow key={m} label={t("Mode d'action")}>
              <span className="text-ui text-ink">{m}</span>
            </VerifyRow>
          ))}
        </Box>
      </section>
      <section>
        <SectionLabel>{t("Unités ennemies citées")}</SectionLabel>
        <Box>
          {enemies.length === 0 && <p className="px-2 py-2 text-ui text-ink-muted">{t("Aucune unité ennemie reconnue : l'ennemi suit la doctrine.")}</p>}
          {enemies.map((u) => {
            const anchor = `enemy:${u.id}`;
            const actions = actionsFor(anchor);
            return (
              <VerifyRow
                key={u.id}
                label={<span className="font-medium text-ink">{unitDesignation(u.designation)}</span>}
                anchor={anchor}
                confidence={u.confidence}
                origin={u.origin}
                source={u.source}
                actions={{ ...actions, onAccept: () => update(acceptProposal(d, actions.check!)), onReject: () => update(rejectProposal(d, actions.check!)) }}
              >
                <UnitMatch unit={u} state={state} update={update} />
                {u.place && <p className="mt-1 text-meta text-ink-muted">{t("Lieu : {place}", { place: placeLabel(places.get(u.place)) })}</p>}
              </VerifyRow>
            );
          })}
        </Box>
      </section>
    </Motion>
  );
}

/** Limites entre unités et graphiques nommés lus dans le texte (docs/20 §14.4) : gardés sur la carte du dossier, ou
 *  écartés. */
function ReadGraphics({ state, update }: Omit<TabProps, "actionsFor">) {
  const d = state.draft;
  const boundaries = d.boundaries ?? [];
  const graphics = d.graphics ?? [];
  const places = new Map(d.places.map((p) => [p.id, p]));
  const placed = (ids: string[]) => ids.filter((id) => places.get(id)?.point).length;
  // Libellés accordés : une limite est « Tracée » ou « Écartée », un graphique « Tracé » ou « Écarté ».
  const keepSelect = (
    value: "keep" | "ignore",
    label: string,
    labels: Record<"keep" | "ignore", string>,
    onChange: (v: "keep" | "ignore") => void,
  ) => (
    <Select value={value} aria-label={label} onChange={(e) => onChange(e.target.value as "keep" | "ignore")}>
      <option value="keep">{t(labels.keep)}</option>
      <option value="ignore">{t(labels.ignore)}</option>
    </Select>
  );
  return (
    <>
      <Presence show={boundaries.length > 0}>
        {boundaries.length > 0 && (
          <Motion origin="fade">
            <section>
              <SectionLabel>{t("Limites entre unités")}</SectionLabel>
              <Box>
                {boundaries.map((b) => (
                  <VerifyRow
                    key={b.id}
                    label={<span className="font-medium text-ink">{boundaryLabel(b)}</span>}
                    confidence={b.confidence}
                    source={b.source}
                    flagged={b.status === "keep" && b.points.length < 2}
                  >
                    <p className="text-meta leading-snug text-ink-soft">{b.texts.join(" – ") || "—"}</p>
                    <p className="mt-0.5 text-meta leading-snug text-ink-muted">
                      {b.points.length >= 2
                        ? t("{n} point(s) placé(s) sur {m}", { n: b.points.length, m: b.places.length })
                        : t("Lieux à placer (onglet « Lieux ») pour tracer cette limite")}
                    </p>
                    <div className="mt-1.5 max-w-60">
                      {keepSelect(b.status, t("Limite {name}", { name: boundaryLabel(b) }), BOUNDARY_KEEP, (status) =>
                        update(withBoundary(d, b.id, { status })),
                      )}
                    </div>
                  </VerifyRow>
                ))}
              </Box>
              <p className="mt-1.5 text-meta leading-snug text-ink-muted">
                {t("Chaque limite gardée devient une limite de la carte du dossier : les ordres subordonnés en tirent la bande de chaque unité.")}
              </p>
            </section>
          </Motion>
        )}
      </Presence>
      <Presence show={graphics.length > 0}>
        {graphics.length > 0 && (
          <Motion origin="fade">
            <section>
              <SectionLabel>{t("Graphiques nommés")}</SectionLabel>
              <Box>
                {graphics.map((g) => (
                  <VerifyRow
                    key={g.id}
                    label={
                      <span className="flex flex-col">
                        <span className="font-medium text-ink">{g.name || "—"}</span>
                        <span className="text-meta text-ink-muted">{t(GRAPHIC_KIND[g.kind])}</span>
                      </span>
                    }
                    confidence={g.confidence}
                    source={g.source}
                  >
                    <p className="text-meta leading-snug text-ink-soft">{g.texts.join(" – ") || "—"}</p>
                    <p className="mt-0.5 text-meta leading-snug text-ink-muted">
                      {t("{n} point(s) placé(s) sur {m}", { n: placed(g.places), m: g.places.length })}
                    </p>
                    <div className="mt-1.5 max-w-60">
                      {keepSelect(g.status, t("Graphique {name}", { name: g.name }), GRAPHIC_KEEP, (status) =>
                        update(withGraphic(d, g.id, { status })),
                      )}
                    </div>
                  </VerifyRow>
                ))}
              </Box>
            </section>
          </Motion>
        )}
      </Presence>
    </>
  );
}

function NoCampNotice({ onTerrain }: { onTerrain: () => void }) {
  return (
    <Callout
      tone="info"
      title={t("Choisissez d'abord le camp")}
      action={
        <Button variant="link" highlight onClick={onTerrain}>
          {t("Onglet Terrain")}
        </Button>
      }
    >
      {t("La carte s'affiche une fois le camp de l'ordre choisi.")}
    </Callout>
  );
}

function LimaTab({
  state,
  update,
  actionsFor,
  drawing,
  setDrawing,
  onOverlay,
  onTerrain,
  overlayBusy,
}: TabProps & {
  drawing: boolean;
  setDrawing: (v: boolean) => void;
  onOverlay: (file: File) => void;
  onTerrain: () => void;
  overlayBusy: boolean;
}) {
  const d = state.draft;
  const ctx = state.context;
  const zone = onFreeTerrain(state);
  const input = useRef<HTMLInputElement | null>(null);
  const origin: Record<string, string> = {
    camp: zone ? t("Fuseau automatique du terrain") : t("Fuseau automatique du camp"),
    text: t("Limites lues dans l'ordre"),
    doctrinal: t("Fuseau doctrinal sur l'axe de l'ordre"),
    overlay: t("Calque joint à l'ordre"),
    drawn: t("Tracé à la main"),
  };
  const proposed = actionsFor("lima:corridor_llm");
  const notices = state.context.checks.filter((c) => c.tab === "lima" && c.kind === "notice");
  // Calque attendu : l'avis à confirmer (ou le calque joint) remplace l'avertissement de la lecture.
  const overlayHandled =
    d.corridor.origin === "overlay" || notices.some((c) => c.id === "overlay_missing") || (d.confirmed ?? []).includes("lima:overlay_missing");
  return (
    <Motion origin="fade" className="space-y-4">
      <TabWarnings draft={d} tab="lima" hide={overlayHandled ? ["boundaries_in_overlay"] : []} />
      <section>
        <SectionLabel>{t("Limites")}</SectionLabel>
        <Box>
          <VerifyRow label={t("Fuseau")} anchor="lima:corridor" flagged={ctx.errors.length > 0}>
            <span className="text-ui text-ink">{origin[d.corridor.origin] ?? "—"}</span>
          </VerifyRow>
          {d.corridor.proposed && (
            <VerifyRow
              label={t("Limites proposées")}
              anchor="lima:corridor_llm"
              origin="llm"
              actions={{ ...proposed, onAccept: () => update(acceptProposal(d, proposed.check!)), onReject: () => update(rejectProposal(d, proposed.check!)) }}
            >
              <p className="text-meta leading-snug text-ink-soft">
                {t("Gauche : {left}", { left: d.corridor.left_text.join(" – ") || "—" })}
                <br />
                {t("Droite : {right}", { right: d.corridor.right_text.join(" – ") || "—" })}
              </p>
            </VerifyRow>
          )}
          {notices.map((c) => (
            <VerifyRow key={c.id} label={t("Avis")} anchor={checkAnchor(c)} actions={{ ...actionsFor(checkAnchor(c)) }}>
              <p className="text-meta leading-snug text-ink-soft">{checkLabel(c, d)}</p>
            </VerifyRow>
          ))}
        </Box>
        {d.camp || zone ? (
          <div className="mt-2 flex flex-wrap gap-2">
            {(d.camp || terrainReady(state)) && (
              <Button variant={drawing ? "primary" : "secondary"} size="sm" onClick={() => setDrawing(!drawing)}>
                <PenLine size={14} /> {drawing ? t("Terminer le tracé") : t("Déplacer les limites et les LIMA")}
              </Button>
            )}
            {d.camp && d.corridor.mode === "custom" && (
              <Button variant="secondary" size="sm" onClick={() => update(campCorridor(d))}>
                <RotateCcw size={14} /> {t("Reprendre le fuseau du camp")}
              </Button>
            )}
            <Button variant="secondary" size="sm" onClick={() => input.current?.click()} disabled={overlayBusy} data-attention="opo-overlay">
              {overlayBusy ? <Spinner decorative /> : <FileUp size={14} />} {t("Joindre le calque de l'ordre")}
            </Button>
            <input
              ref={input}
              type="file"
              className="hidden"
              accept=".kmz,.kml,.nvg,.xml,application/vnd.google-earth.kmz,application/vnd.google-earth.kml+xml"
              aria-label={t("Calque de l'ordre (KMZ, KML ou NVG)")}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (file) onOverlay(file);
              }}
            />
          </div>
        ) : (
          <div className="mt-2">
            <NoCampNotice onTerrain={onTerrain} />
          </div>
        )}
        <Presence show={drawing}>
          {drawing && (
            <Motion origin="top">
              <p className="mt-1.5 text-meta leading-snug text-ink-muted">{t("Glissez les poignées bleues : sommets des limites et extrémités des LIMA.")}</p>
            </Motion>
          )}
        </Presence>
        <Presence show={ctx.warnings.length > 0}>
          {ctx.warnings.length > 0 && (
            <Callout tone="info" className="mt-2">
              <ul className="list-disc space-y-0.5 pl-4">
                {ctx.warnings.map((w) => (
                  <li key={w}>{tServer(w)}</li>
                ))}
              </ul>
            </Callout>
          )}
        </Presence>
      </section>
      <Presence show={zone && !terrainReady(state)}>
        {zone && !terrainReady(state) && (
          <Callout tone="info" action={<Button variant="link" onClick={onTerrain}>{t("Onglet Terrain")}</Button>}>
            {t("Le fuseau se pose sur le terrain libre une fois prêt ; un calque joint est déjà lu.")}
          </Callout>
        )}
      </Presence>
      <section>
        <SectionLabel>{t("LIMA")}</SectionLabel>
        <Box>
          {d.lima.length === 0 && <p className="px-2 py-2 text-ui text-ink-muted">{t("Aucune LIMA citée : les LIMA suivent la doctrine.")}</p>}
          {d.lima.map((l) => {
            const anchor = `lima:${l.id}`;
            const actions = actionsFor(anchor);
            return (
              <VerifyRow
                key={l.id}
                label={<span className="font-medium text-ink">{l.name}</span>}
                anchor={anchor}
                confidence={l.confidence}
                origin={l.llm ? "llm" : l.origin}
                source={l.source}
                actions={{ ...actions, onAccept: () => update(acceptProposal(d, actions.check!)), onReject: () => update(rejectProposal(d, actions.check!)) }}
              >
                <div className="grid gap-1.5 sm:grid-cols-[7rem_minmax(0,1fr)]">
                  <Select
                    value={l.slot ?? ""}
                    aria-label={t("Rang de {name}", { name: l.name })}
                    onChange={(e) => update(withLima(d, l.id, { slot: (e.target.value || null) as typeof l.slot }))}
                  >
                    {(["L0", "L1", "L2", "L3"] as const).map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                    <option value="">{t("Graphique de carte")}</option>
                  </Select>
                  <TextInput small value={l.dtg} ariaLabel={t("GDH de {name}", { name: l.name })} placeholder={t("GDH")} onCommit={(dtg) => update(withLima(d, l.id, { dtg }))} />
                </div>
                {l.slot === null && <p className="mt-1 text-meta text-ink-muted">{limaSlotLabel(l)}</p>}
              </VerifyRow>
            );
          })}
        </Box>
      </section>
      <ReadGraphics state={state} update={update} />
    </Motion>
  );
}

function PlacesTab({
  state,
  update,
  actionsFor,
  pointing,
  setPointing,
  setHovered,
  onTerrain,
}: TabProps & {
  pointing: string | null;
  setPointing: (id: string | null) => void;
  setHovered: (id: string | null) => void;
  onTerrain: () => void;
}) {
  const d = state.draft;
  const flags = state.context.place_flags ?? {};
  const zone = onFreeTerrain(state);
  const waiting = zone && !terrainReady(state);
  // Pointage : sur la carte d'un camp, ou sur le globe du terrain libre une fois situé (emprise ou terrain).
  const canPoint = Boolean(d.camp) || (zone && Boolean(state.context.map.bounds));
  return (
    <Motion origin="fade" className="space-y-4">
      <section>
        <SectionLabel>{t("Lieux cités")}</SectionLabel>
        <p className="mt-1 text-meta leading-snug text-ink-muted">
          {zone
            ? t("Tout est résolu sur le serveur, à partir des données du terrain libre préparé : aucun nom de lieu n'est envoyé à l'extérieur.")
            : t("Tout est résolu sur le serveur, à partir des données du camp : aucun lieu n'est envoyé à l'extérieur.")}
        </p>
        {!d.camp && !zone && (
          <div className="mt-2">
            <NoCampNotice onTerrain={onTerrain} />
          </div>
        )}
        <Presence show={waiting}>
          {waiting && (
            <Callout tone="info" className="mt-2" action={<Button variant="link" onClick={onTerrain}>{t("Onglet Terrain")}</Button>}>
              {t("Les coordonnées citées sont déjà placées ; les noms de lieux seront résolus sur les données du terrain libre dès qu'il sera prêt.")}
            </Callout>
          )}
        </Presence>
        <Box>
          {d.places.length === 0 && <p className="px-2 py-2 text-ui text-ink-muted">{t("Aucun lieu cité.")}</p>}
          {d.places.map((p) => {
            const anchor = `places:${p.id}`;
            return (
              <div key={p.id} onMouseEnter={() => setHovered(p.id)} onMouseLeave={() => setHovered(null)}>
                <VerifyRow
                  label={<span className="font-medium text-ink">{placeLabel(p)}</span>}
                  anchor={anchor}
                  confidence={p.confidence}
                  source={p.source}
                  sources={p.sources}
                  flagged={!p.point && !(waiting && !p.coordinate)}
                  actions={actionsFor(anchor)}
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={clsx("text-ui", p.point || (waiting && !p.coordinate) ? "text-ink" : "text-warn")}>{placeHow(p, zone, waiting)}</span>
                    {p.coordinate && <span className="font-mono text-meta text-ink-soft tabular-nums">{p.coordinate}</span>}
                    {p.name && p.name !== p.text && <span className="text-meta text-ink-muted">{p.name}</span>}
                    {p.precision_m !== null && p.point && (
                      <span className="text-meta text-ink-muted tabular-nums">{t("± {m} m", { m: Math.round(p.precision_m) })}</span>
                    )}
                    {p.note && <span className="text-meta text-ink-muted">{tServer(p.note)}</span>}
                    {flags[p.id] === "outside" && <span className="text-meta text-warn">{t("hors du fuseau de l'ordre")}</span>}
                    {canPoint && (
                      <Button variant={pointing === p.id ? "primary" : "secondary"} size="sm" onClick={() => setPointing(pointing === p.id ? null : p.id)}>
                        <Crosshair size={14} /> {pointing === p.id ? t("Cliquez sur la carte") : p.point ? t("Pointer ailleurs") : t("Pointer sur la carte")}
                      </Button>
                    )}
                  </div>
                  {p.candidates.length > 1 && (
                    <div className="mt-1 flex flex-col items-start gap-0.5">
                      {p.candidates.map((c, i) => (
                        <Button
                          key={`${c.name}-${c.point.join(",")}`}
                          variant="link"
                          onClick={() => update(withPlace(d, p.id, { point: c.point, how: "clicked", name: c.name, confidence: "certain", candidates: [] }))}
                        >
                          <MapPin size={12} /> {`${i + 1}. ${candidateLabel(d, p, c)}`}
                        </Button>
                      ))}
                    </div>
                  )}
                </VerifyRow>
              </div>
            );
          })}
        </Box>
      </section>
    </Motion>
  );
}

function PhasesTab({ state, update, actionsFor }: TabProps) {
  const d = state.draft;
  const llm = state.context.checks.find((c) => c.tab === "phases" && c.kind === "llm");
  const needsH = state.context.checks.some((c) => c.id === "h_hour") || Boolean(d.h_hour);
  return (
    <Motion origin="fade" className="space-y-4">
      <TabWarnings draft={d} tab="phases" />
      <TabChecks state={state} tab="phases" actionsFor={actionsFor} />
      <section>
        <SectionLabel>{t("Début de l'exercice")}</SectionLabel>
        <Box>
          <VerifyRow label={t("Début (heure locale)")} anchor="phases:start" confidence={d.start_confidence} source={d.start_source} flagged={!d.start_dtg}>
            <input
              type="datetime-local"
              className={inputClass}
              value={d.start_dtg ? d.start_dtg.slice(0, 16) : ""}
              aria-label={t("Début (heure locale)")}
              onChange={(e) => update({ ...d, start_dtg: e.target.value ? `${e.target.value}:00` : null, start_confidence: "certain" })}
            />
          </VerifyRow>
          {needsH && (
            <VerifyRow label={t("Heure H (heure locale)")} anchor="phases:h_hour" confidence={d.h_confidence} source={d.h_source} flagged={!d.h_hour}>
              <input
                type="datetime-local"
                className={inputClass}
                value={d.h_hour ? d.h_hour.slice(0, 16) : ""}
                aria-label={t("Heure H (heure locale)")}
                onChange={(e) => update({ ...d, h_hour: e.target.value ? `${e.target.value}:00` : null, h_confidence: "certain" })}
              />
              <p className="mt-1 text-meta text-ink-muted">{t("Les LIMA « H », « H+6 »… se comptent depuis cette heure.")}</p>
            </VerifyRow>
          )}
          <VerifyRow label={t("Fuseau horaire")}>
            <span className="font-mono text-ui text-ink">{d.time_zone}</span>
          </VerifyRow>
        </Box>
      </section>
      <section>
        <SectionLabel>{t("Météo")}</SectionLabel>
        <Box>
          <OrderWeather state={state} update={update} actionsFor={actionsFor} />
        </Box>
      </section>
      <section>
        <SectionLabel>{t("Phases de l'ordre")}</SectionLabel>
        {llm && (
          <div className="mt-1.5 flex flex-wrap items-center gap-2" data-check={checkAnchor(llm)}>
            <Badge tone="run" size="sm">{t("Proposé par l'IA")}</Badge>
            <CheckActions check={llm} onAccept={() => update(acceptProposal(d, llm))} onReject={() => update(rejectProposal(d, llm))} />
          </div>
        )}
        <Box>
          {d.phases.length === 0 && <p className="px-2 py-2 text-ui text-ink-muted">{t("Aucune phase reconnue : les temps suivent la doctrine.")}</p>}
          {d.phases.map((p) => (
            <VerifyRow key={`${p.name}-${p.index}`} label={<span className="font-medium text-ink">{p.name}</span>} confidence={p.confidence} origin={p.origin} source={p.source}>
              <div className="grid gap-1.5 sm:grid-cols-2">
                <TextInput small value={p.start} ariaLabel={t("Début de {name}", { name: p.name })} placeholder={t("Début (GDH)")} onCommit={(start) => update(withPhase(d, p.index, { start }))} />
                <TextInput small value={p.end} ariaLabel={t("Fin de {name}", { name: p.name })} placeholder={t("Fin (GDH)")} onCommit={(end) => update(withPhase(d, p.index, { end }))} />
              </div>
              {p.title && <p className="mt-1 text-meta leading-snug text-ink-soft">{p.title}</p>}
              {p.index > 3 && <p className="mt-0.5 text-meta text-ink-muted">{t("Au-delà du troisième temps : gardée en graphique de carte.")}</p>}
            </VerifyRow>
          ))}
        </Box>
      </section>
    </Motion>
  );
}

/** Météo de l'ordre (sous-paragraphe Météo lu) : ce que l'ordre dit, puis ce que le dossier en garde — la météo de
 *  l'ordre (d'office quand la lecture est sûre) ou celle de la saison, ramenée au terrain et à la date de
 *  l'exercice ; une météo illisible, ou changée par le terrain, se confirme sur sa ligne. */
function OrderWeather({ state, update, actionsFor }: TabProps) {
  const d = state.draft;
  const weather = weatherOf(d);
  const view = weatherView(state.context);
  if (!weather?.found || !view.found) {
    return <p className="px-2 py-2 text-ui text-ink-muted">{t("Aucune météo dans l'ordre : météo de saison du terrain, modifiable dans les Données initiales.")}</p>;
  }
  if (view.state === "unread") {
    return (
      <VerifyRow label={t("Météo de l'ordre")} anchor="phases:weather_unread" source={weather.source} actions={actionsFor("phases:weather_unread")}>
        <p className="text-meta leading-snug text-ink-soft">{weatherOutcome(view)}</p>
      </VerifyRow>
    );
  }
  const seasonal = weather.key === "auto";
  // Choix de la vérification montré aussitôt (le serveur relit ensuite la météo du dossier).
  const take = weather.take ?? view.take ?? false;
  const undecided = weatherUndecided(d, state.context);
  const changed = take && (view.state === "adjusted" || view.state === "impossible");
  return (
    <>
      <VerifyRow label={t("Météo de l'ordre")} confidence={weather.confidence} source={weather.source}>
        <span className="text-ui text-ink">{weatherLabel(view.label)}</span>
      </VerifyRow>
      <VerifyRow
        label={t("Dans le dossier")}
        anchor={changed ? "phases:weather_terrain" : "phases:weather"}
        flagged={undecided}
        actions={changed ? actionsFor("phases:weather_terrain") : undefined}
      >
        {!seasonal && (
          <Segmented
            ariaLabel={t("Météo du dossier")}
            size="sm"
            fill="natural"
            options={[
              { value: "order", label: t("Météo de l'ordre") },
              { value: "season", label: seasonalLabel() },
            ]}
            value={take ? "order" : "season"}
            onChange={(choice) => update(withWeatherTaken(d, choice === "order"))}
          />
        )}
        {take && view.take && view.applied_label && view.applied_label !== "auto" && view.applied_label !== view.label && (
          <p className="mt-1 text-ui text-ink">{view.applied_label}</p>
        )}
        <p className={clsx("mt-1 text-meta leading-snug", changed ? "text-warn" : "text-ink-muted")}>{weatherOutcome(view)}</p>
      </VerifyRow>
    </>
  );
}

function MissionsTab({ state, update, actionsFor }: TabProps) {
  const d = state.draft;
  const catalogue = state.context.catalogue.AMI ?? [];
  const applied = state.context.applied ?? {};
  const places = new Map(d.places.map((p) => [p.id, p]));
  const friends = unitsOf(d, "AMI").filter((u) => u.status !== "ignore");
  return (
    <Motion origin="fade" className="space-y-4">
      <TabChecks state={state} tab="missions" actionsFor={actionsFor} />
      {friends.map((u) => (
        <section key={u.id}>
          <SectionLabel>{unitDesignation(u.designation)}</SectionLabel>
          <Box>
            {PHASES.map((phase: Phase) => {
              const task = u.tasks[phase];
              const anchor = `missions:${u.id}:${phase}`;
              const actions = actionsFor(anchor);
              const onMap = applied[u.id]?.[phase];
              return (
                <VerifyRow
                  key={phase}
                  label={phase}
                  anchor={anchor}
                  confidence={task?.station ? null : task?.confidence}
                  origin={task?.origin}
                  source={task?.source}
                  flagged={Boolean(task?.mapped)}
                  actions={{ ...actions, onAccept: () => update(acceptProposal(d, actions.check!)), onReject: () => update(rejectProposal(d, actions.check!)) }}
                >
                  <Select
                    value={task?.mission ?? ""}
                    aria-label={t("Mission de {unit} en {phase}", { unit: unitDesignation(u.designation), phase })}
                    onChange={(e) => update(withTask(d, u.id, phase, { mission: e.target.value || null, mapped: false }))}
                  >
                    <option value="">{t("Mission du dispositif")}</option>
                    {catalogue.map((code) => (
                      <option key={code} value={code}>
                        {unitMissionName(code)}
                      </option>
                    ))}
                  </Select>
                  {task && (task.verb || task.place) && (
                    <p className="mt-1 text-meta leading-snug text-ink-muted">
                      {task.station && task.code
                        ? t("Lu : « {verb} » (réserve : portée par le rôle « Renforts »)", { verb: task.verb })
                        : task.station
                        ? t("Stationnement : {place}", { place: placeLabel(places.get(task.place ?? "")) })
                        : task.mapped
                          ? t("« {verb} » n'est pas une mission de ce niveau : mission la plus proche retenue.", { verb: task.verb })
                          : task.code && !task.mission
                            ? t("« {verb} » n'a pas de mission sur la carte : choisissez-en une, sinon la mission du dispositif est gardée.", { verb: task.verb })
                            : t("Lu : « {verb} »", { verb: task.verb })}
                      {task.place && !task.station && ` · ${placeLabel(places.get(task.place))}`}
                      {task.target && ` · ${t("cible : {unit}", { unit: task.target })}`}
                    </p>
                  )}
                  {onMap !== undefined && (
                    <p className="mt-0.5 text-meta leading-snug text-warn">
                      {t("Sur la carte : {mission} (cette unité ne porte pas la mission de l'ordre à ce temps)", { mission: onMap ? unitMissionName(onMap) : "—" })}
                    </p>
                  )}
                </VerifyRow>
              );
            })}
          </Box>
          {u.contingent.length > 0 && (
            <p className="mt-1 text-meta leading-snug text-ink-muted">{t("Tâches éventuelles : {tasks}", { tasks: u.contingent.join(", ") })}</p>
          )}
        </section>
      ))}
    </Motion>
  );
}

// --- Fenêtre --------------------------------------------------------------------------------------------------

export function OpoVerificationDialog({
  initial,
  onClose,
  onCreated,
}: {
  initial: OpoDraftState;
  onClose: () => void;
  onCreated: (ex: Exercise) => void | Promise<void>;
}) {
  const [state, setState] = useState(initial);
  const [tab, setTab] = useState<OpoTab>(() => (nextCheck(initial.context.checks)?.tab ?? "issuer"));
  const [phase, setPhase] = useState<Phase>("T1");
  const [busy, setBusy] = useState<Busy>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [pointing, setPointing] = useState<string | null>(null);
  const [hovered, setHovered] = useState<string | null>(null);
  const [drawing, setDrawing] = useState(false);
  const [mapMode, setMapMode] = useState<TerrainMapMode>(null);
  // Version du brouillon dont la préparation a été refusée (une autre se prépare pour la licence) : « Préparer le
  // terrain » ne pulse plus tant que rien ne change.
  const [prepareRefusedAt, setPrepareRefusedAt] = useState<number | null>(null);
  const [confirming, setConfirming] = useState(false);
  const queryClient = useQueryClient();
  const [ai, setAi] = useState<{ step: number; total: number; source: string | null } | null>(null);
  const abort = useRef<AbortController | null>(null);
  const mapBox = useRef<HTMLDivElement | null>(null);
  // Enregistrements l'un après l'autre : la dernière correction attend la réponse de la précédente.
  const queue = useRef<{ running: boolean; next: OpoDraft | null }>({ running: false, next: null });
  const latest = useRef(state);
  latest.current = state;

  const reload = async () => {
    try {
      setState(await api.opoDraft(latest.current.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : t("Enregistrement impossible : le serveur ne répond pas."));
    }
  };

  const flush = async () => {
    if (queue.current.running || !queue.current.next) return;
    const draft = queue.current.next;
    queue.current = { running: true, next: null };
    setBusy("save");
    try {
      const saved = await api.saveOpoDraft(latest.current.id, latest.current.version, draft);
      if (!queue.current.next) setState(saved);
      else setState({ ...saved, draft: queue.current.next });
      latest.current = { ...saved, draft: queue.current.next ?? saved.draft };
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        await reload();
        setNotice(t("Cet import a été modifié sur un autre poste : la vérification a été rechargée."));
      } else setError(err instanceof Error ? err.message : t("Enregistrement impossible : le serveur ne répond pas."));
    } finally {
      queue.current.running = false;
      setBusy((b) => (b === "save" ? null : b));
      if (queue.current.next) void flush();
    }
  };

  const update = (next: OpoDraft) => {
    if (abort.current) return; // corrections suspendues pendant la lecture de l'IA
    setError(null);
    setState((s) => ({ ...s, draft: next }));
    queue.current.next = next;
    void flush();
  };

  /** Fin des enregistrements en cours (la version du brouillon est alors celle du serveur). */
  const settled = () =>
    new Promise<void>((resolve) => {
      const tick = () => (!queue.current.running && !queue.current.next ? resolve() : window.setTimeout(tick, 100));
      tick();
    });

  /** Action sur le terrain libre de l'import, après les enregistrements en cours ; l'avis du serveur est montré. */
  const terrainDo = async (action: "prepare" | "reuse" | "extend" | "retry" | "release", extra: { zone_id?: string } = {}) => {
    await settled();
    setBusy("terrain");
    setError(null);
    try {
      const current = latest.current;
      const axis = terrainOf(current.draft).axis;
      const name = t("Terrain — {name}", { name: current.draft.name }).slice(0, 80);
      const body = action === "prepare" ? { ...extra, name, ...(axis ? { axis } : {}) } : extra;
      const res = await api.opoDraftTerrain(current.id, current.version, action, body);
      const { notice: code, ...next } = res;
      setState(next);
      latest.current = next;
      setMapMode(null);
      setPrepareRefusedAt(null);
      // « Préparation lancée » n'est pas répété : l'encadré de la préparation le dit.
      setNotice(TERRAIN_NOTICE[code] && !QUIET_NOTICES.has(code) ? t(TERRAIN_NOTICE[code]!) : null);
      refreshTerrain(queryClient);
    } catch (err) {
      setNotice(null);
      // Terrain délié entre-temps (supprimé, rendu privé) ou brouillon modifié ailleurs : la vérification est relue.
      if (err instanceof ApiError && err.status === 409) await reload();
      if (err instanceof ApiError && err.status === 429 && action === "prepare") {
        setPrepareRefusedAt(latest.current.version);
        setError(
          `${err.message} ${t("La préparation en cours se suit, et s'annule si besoin, dans la vue Manœuvre (terrains libres) ; relancez celle-ci ensuite.")}`,
        );
      } else setError(err instanceof Error ? err.message : t("Enregistrement impossible : le serveur ne répond pas."));
    } finally {
      setBusy((b) => (b === "terrain" ? null : b));
    }
  };

  const checks = state.context.checks;
  const counts = useMemo(() => checksByTab(checks), [checks]);
  const next = nextCheck(checks);
  // Terrain en préparation : dit à part, le point actionnable suivant reste désigné.
  const waitingCheck = pendingCheck(checks);
  const idle = busy === null && ai === null;
  const ready = state.context.ready && idle;

  const actionsFor = (anchor: string): RowActions => {
    const check = checkAt(checks, anchor);
    if (!check) return {};
    return { check, onConfirm: () => update(confirm(state.draft, check)) };
  };

  const goTo = (check: OpoCheck | null) => {
    if (!check) return;
    setTab(check.tab);
    designateSoon(`[data-check="${checkAnchor(check)}"]`, "ring");
  };

  const createDossier = async () => {
    setBusy("create");
    setError(null);
    try {
      await onCreated(await api.createFromOpoDraft(state.id, state.version));
    } catch (err) {
      setError(err instanceof Error ? err.message : t("Création impossible : le serveur ne répond pas."));
      setBusy(null);
    }
  };

  const abandon = async () => {
    setBusy("delete");
    try {
      await api.deleteOpoDraft(state.id);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("Abandon impossible : le serveur ne répond pas."));
      setBusy(null);
    }
  };

  const joinOverlay = async (file: File) => {
    setBusy("overlay");
    setError(null);
    try {
      setState(await api.opoDraftOverlay(state.id, state.version, file));
      setNotice(t("Calque joint : son fuseau et ses LIMA remplacent le fuseau proposé."));
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) await reload();
      setError(err instanceof Error ? err.message : t("Calque illisible : joignez un fichier KMZ, KML ou NVG exporté par un outil de carte."));
    } finally {
      setBusy(null);
    }
  };

  const propose = async () => {
    const controller = new AbortController();
    abort.current = controller;
    setAi({ step: 0, total: 0, source: null });
    setError(null);
    setNotice(null);
    try {
      await streamOpoProposals(
        state.id,
        (e) => {
          if (e.event === "progress") setAi((a) => ({ step: e.data.step ?? 0, total: e.data.total ?? 0, source: a?.source ?? null }));
          else if (e.event === "source") setAi((a) => (a ? { ...a, source: e.data.label ?? null } : a));
          else if (e.event === "error") {
            setError(tServer(e.data.message ?? ""));
            if (e.data.reload) void reload();
          } else if (e.event === "done") {
            if (e.data.draft && e.data.context && e.data.version) setState((s) => ({ ...s, ...(e.data as OpoDraftState) }));
            setNotice(
              e.data.nothing
                ? t("Rien à proposer : la lecture par règles est complète.")
                : t("{n} proposition(s) de l'IA à vérifier : validez-les ou écartez-les sur leur ligne.", { n: e.data.count ?? 0 }),
            );
          }
        },
        controller.signal,
      );
    } catch (err) {
      if (!controller.signal.aborted) setError(err instanceof Error ? err.message : t("IA indisponible."));
    } finally {
      abort.current = null;
      setAi(null);
    }
  };

  useEffect(() => () => abort.current?.abort(), []);
  // Fin du pointage et des tracés quand on quitte leur onglet.
  useEffect(() => {
    if (tab !== "places") setPointing(null);
    if (tab !== "lima") setDrawing(false);
    if (tab !== "terrain") setMapMode(null);
  }, [tab]);
  // Pointage ou tracé commencé : la carte revient dans la vue (au-dessus de la liste sur un téléphone).
  useEffect(() => {
    if (pointing || drawing || mapMode) mapBox.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [pointing, drawing, mapMode]);

  // Import en attente du terrain libre : la zone est relevée (1,5 s) ; prête ou en échec, la vérification est relue
  // (lieux résolus sur les données du terrain, fuseau, carte peuplée), après les enregistrements en cours.
  const terrainCtx = state.context.terrain;
  const waiting = terrainWaiting(terrainCtx);
  const linkedId = terrainCtx?.zone?.id ?? null;
  const { data: polled, error: pollError } = useZone(waiting ? linkedId : null, true);
  const resumed = useRef<string | null>(null);
  // Terrain lié supprimé ou devenu invisible pendant sa préparation (vue Manœuvre, autre compte) : le relevé échoue,
  // la vérification est relue (le serveur l'a délié) et l'utilisateur est prévenu.
  useEffect(() => {
    if (!waiting || !linkedId || !(pollError instanceof ApiError) || ![403, 404].includes(pollError.status)) return;
    const key = `${linkedId}:gone`;
    if (resumed.current === key) return;
    resumed.current = key;
    void (async () => {
      await settled();
      if (abort.current) return;
      await reload();
      refreshTerrain(queryClient);
      setNotice(t("Le terrain lié à cet import n'existe plus ou ne vous est plus visible : préparez-en un autre."));
    })();
  }, [waiting, linkedId, pollError]);
  useEffect(() => {
    if (!waiting || !polled || polled.id !== linkedId || zoneBusy(polled)) return;
    const key = `${polled.id}:${polled.status}:${polled.pending_status ?? ""}`;
    if (resumed.current === key) return;
    resumed.current = key;
    void (async () => {
      await settled();
      if (abort.current) return;
      await reload();
      refreshTerrain(queryClient);
      setNotice(
        polled.status === "ready"
          ? t("Terrain prêt : les lieux de l'ordre sont résolus sur ses données, et la carte est peuplée.")
          : t("La préparation du terrain n'a pas abouti : relancez-la dans l'onglet « Terrain »."),
      );
    })();
  }, [waiting, polled, linkedId]);

  const zoneMode = onFreeTerrain(state);
  const showMap = MAP_TABS.has(tab) && (Boolean(state.draft.camp) || zoneMode);
  const props = { state, update, actionsFor };
  const toTerrain = () => setTab("terrain");
  // Action attendue de l'onglet « Terrain » (préparer, confirmer la zone de grille, relancer, étendre…) : désignée sur
  // place quand l'onglet est ouvert, « Voir » ne l'est plus (une seule action désignée dans la fenêtre).
  const terrainInPlace = tab === "terrain" && next?.tab === "terrain" && terrainAction(terrainCtx) !== null;
  const terrainProps: TerrainProps = {
    mapMode,
    setMapMode,
    onAction: (action, extra) => void terrainDo(action, extra),
    acting: busy === "terrain",
    zone: polled && polled.id === linkedId ? polled : null,
    onIssuer: () => setTab("issuer"),
    designated: idle && terrainInPlace,
    prepareRefused: prepareRefusedAt !== null && prepareRefusedAt === state.version,
  };
  const content: Record<OpoTab, ReactNode> = {
    issuer: <IssuerTab {...props} />,
    units: <UnitsTab {...props} />,
    terrain: <TerrainTab {...props} {...terrainProps} />,
    enemy: <EnemyTab {...props} />,
    lima: (
      <LimaTab
        {...props}
        drawing={drawing}
        setDrawing={setDrawing}
        onOverlay={(file) => void joinOverlay(file)}
        onTerrain={toTerrain}
        overlayBusy={busy === "overlay"}
      />
    ),
    places: <PlacesTab {...props} pointing={pointing} setPointing={setPointing} setHovered={setHovered} onTerrain={toTerrain} />,
    phases: <PhasesTab {...props} />,
    missions: <MissionsTab {...props} />,
  };
  const pointedPlace = pointing ? state.draft.places.find((p) => p.id === pointing) : undefined;

  return (
    <>
    <Dialog
      title={t("Vérifier l'OPO importé")}
      subtitle={state.filename ?? t("Texte collé")}
      width={1180}
      onClose={onClose}
      actions={
        <>
          <Button variant="ghost" size="sm" onClick={() => (ai ? abort.current?.abort() : void propose())} disabled={busy !== null}>
            {ai ? <Square size={14} /> : <Sparkles size={14} />} {ai ? t("Arrêter l'IA") : t("Proposer avec l'IA")}
          </Button>
          <HelpButton screen="accueil" topic="accueil.verifier-opo" buttonSize="sm" size={16} />
        </>
      }
      footer={
        <>
          <Button
            variant="danger-ghost"
            onClick={() => setConfirming(true)}
            disabled={busy !== null && busy !== "save"}
            className="mr-auto"
            aria-label={t("Abandonner l'import")}
            title={t("Abandonner l'import")}
          >
            <Trash2 size={14} /> <span className="hidden sm:inline">{t("Abandonner l'import")}</span>
          </Button>
          <Button variant="secondary" onClick={onClose} disabled={busy === "create"}>
            {t("Reprendre plus tard")}
          </Button>
          <Button
            onClick={() => void createDossier()}
            highlight={ready && checks.length === 0}
            blocked={!state.context.ready || ai !== null}
            onBlocked={() => goTo(nextCheck(checks.filter((c) => c.required)) ?? next)}
            disabled={busy === "create" || busy === "delete"}
            data-attention="opo-verify-create"
          >
            {busy === "create" ? <Spinner decorative /> : <FolderPlus size={14} />}
            {busy === "create" ? <Waiting>{t("Création du dossier…")}</Waiting> : t("Créer le dossier")}
          </Button>
        </>
      }
    >
      <div className="sticky top-0 z-10 border-b border-line bg-surface px-4 py-2">
        <Tabs
          ariaLabel={t("Rubriques de la vérification")}
          tabs={TABS.map((tb) => ({
            value: tb.value,
            label: t(tb.label),
            count: counts[tb.value].count || undefined,
            dot: counts[tb.value].required ? "var(--color-warn)" : undefined,
          }))}
          value={tab}
          onChange={setTab}
        />
      </div>
      <div className="space-y-3 px-4 py-4">
        <p className="text-meta leading-snug text-ink-muted">
          {t("Chaque correction est enregistrée : l'import se rouvre tel quel jusqu'à la création du dossier.")}
        </p>
        <Presence show={Boolean(error)}>{error && <Callout tone="danger">{tServer(error)}</Callout>}</Presence>
        <Presence show={Boolean(notice)}>{notice && <Callout tone="info" onClose={() => setNotice(null)}>{notice}</Callout>}</Presence>
        <Presence show={ai !== null}>
          {ai && (
            <Callout tone="info" icon={Sparkles} title={<Waiting>{t("Le modèle local relit l'ordre…")}</Waiting>}>
              {ai.total ? t("Demande {step} sur {total}", { step: ai.step, total: ai.total }) : <Waiting>{t("Préparation des demandes…")}</Waiting>}
              {ai.source && ` · ${tServer(ai.source)}`}
              <span className="mt-0.5 block">{t("Les corrections reprennent à la fin de la lecture.")}</span>
            </Callout>
          )}
        </Presence>
        <Presence show={state.context.errors.length > 0}>
          {state.context.errors.length > 0 && (
            <Callout tone="warn" title={t("À reprendre avant la création")}>
              <ul className="list-disc space-y-0.5 pl-4">
                {state.context.errors.map((e) => (
                  <li key={e}>{tServer(e)}</li>
                ))}
              </ul>
            </Callout>
          )}
        </Presence>
        <Presence show={Boolean(next)}>
          {next && (
            <Callout
              // Seul point restant : l'attente du terrain, une attente et non une alerte ; « Voir » mène à son avancement
              // sans être une action attendue. Sinon le point actionnable suivant, désigné, et l'attente dite en bas.
              tone={next.required && next.kind !== "pending" ? "warn" : "info"}
              title={next.kind === "pending" ? t("En attente du terrain libre") : next.required ? t("À régler avant la création") : t("À vérifier ensuite")}
              action={
                <Button variant="link" highlight={idle && !terrainInPlace && next.kind !== "pending"} onClick={() => goTo(next)}>
                  {t("Voir")}
                </Button>
              }
            >
              {checkLabel(next, state.draft)}
              {checks.length > 1 && ` · ${t("{n} point(s) à vérifier", { n: checks.length })}`}
              {!next.required && state.context.ready && ` · ${t("le dossier peut déjà être créé")}`}
              {waitingCheck && next.kind !== "pending" && (
                <span className="mt-0.5 block text-ink-muted">{checkLabel(waitingCheck, state.draft)}</span>
              )}
            </Callout>
          )}
        </Presence>
        <Presence show={!next && state.context.ready}>
          {!next && state.context.ready && (
            <Callout tone="ok" title={t("Vérification complète")}>
              {t("Le dossier peut être créé : sa carte sera peuplée comme l'aperçu le montre.")}
            </Callout>
          )}
        </Presence>
        <div className={clsx("grid gap-4", showMap && "lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]")}>
          <div role="tabpanel" className="min-w-0">
            <fieldset disabled={ai !== null} className="min-w-0">
              {content[tab]}
            </fieldset>
          </div>
          {showMap && (
            <div className="order-first lg:order-none" ref={mapBox}>
              <div className="lg:sticky lg:top-14">
                <div className="relative">
                  <VerificationMap
                    draft={state.draft}
                    context={state.context}
                    phase={phase}
                    onPhase={setPhase}
                    mode={
                      ai
                        ? "view"
                        : tab === "places" && pointing
                          ? "point"
                          : tab === "lima" && drawing
                            ? "draw"
                            : tab === "terrain" && mapMode
                              ? mapMode
                              : "view"
                    }
                    selectedPlace={pointing ?? (tab === "places" ? hovered : null)}
                    onPoint={(point) => {
                      if (!pointing) return;
                      update(pointed(state.draft, pointing, point));
                      setPointing(null);
                    }}
                    onVertex={(vertex, point) => update(movedVertex(state.draft, vertex, point))}
                    onAnchor={(point) => {
                      update(withTerrain(state.draft, { anchor: [Math.round(point[0] * 1e6) / 1e6, Math.round(point[1] * 1e6) / 1e6] }));
                      setMapMode(null);
                    }}
                    onAxis={(axis) => {
                      update(withTerrain(state.draft, { axis }));
                      if (mapMode === "axis") setMapMode(null);
                    }}
                    className="h-72 lg:h-[30rem]"
                  />
                  {/* Consigne du pointage : en haut de la carte, sous T1/T2/T3, à l'écart du zoom et du logo du fond (en bas) ;
                      elle passe à la ligne, plus courte sur un téléphone, et « Annuler » reste entier. */}
                  <Presence show={tab === "terrain" && (mapMode === "anchor" || mapMode === "axis")}>
                    {tab === "terrain" && (mapMode === "anchor" || mapMode === "axis") && (
                      <Motion
                        origin="top"
                        data-map-hint="terrain"
                        className="absolute inset-x-2 top-12 flex items-center gap-2 rounded-lg bg-surface/95 px-3 py-2 text-ui shadow-sm"
                      >
                        <Crosshair size={14} className="shrink-0 text-accent" />
                        <span className="min-w-0 flex-1 leading-snug">
                          {mapMode === "anchor" ? (
                            t("Pointez un lieu du terrain de l'ordre")
                          ) : (
                            <>
                              <span className="sm:hidden">{t("Arrière, puis avant côté ennemi")}</span>
                              <span className="hidden sm:inline">{t("Cliquez l'arrière de la manœuvre, puis son avant côté ennemi")}</span>
                            </>
                          )}
                        </span>
                        <Button variant="link" className="shrink-0" onClick={() => setMapMode(null)}>
                          {t("Annuler")}
                        </Button>
                      </Motion>
                    )}
                  </Presence>
                  <Presence show={Boolean(pointedPlace)}>
                    {pointedPlace && (
                      <Motion origin="top" className="absolute inset-x-2 top-12 flex items-center gap-2 rounded-lg bg-surface/95 px-3 py-2 text-ui shadow-sm">
                        <Crosshair size={14} className="shrink-0 text-accent" />
                        <span className="min-w-0 flex-1 leading-snug break-words">{t("Pointez « {name} » sur la carte", { name: placeLabel(pointedPlace) })}</span>
                        <Button variant="link" className="shrink-0" onClick={() => setPointing(null)}>
                          {t("Annuler")}
                        </Button>
                      </Motion>
                    )}
                  </Presence>
                </div>
                <p className="mt-1 text-meta leading-snug text-ink-muted">
                  {busy === "save"
                    ? <Waiting>{t("Relecture…")}</Waiting>
                    : zoneMode && !terrainReady(state)
                      ? t("Aperçu du terrain libre de l'ordre : la carte se peuple dès qu'il est prêt.")
                      : t("Aperçu de la carte que le dossier aura, au temps choisi.")}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </Dialog>
    <Presence show={confirming}>
      {confirming && (
        <ConfirmDialog
          title={t("Abandonner cet import ?")}
          confirmLabel={t("Abandonner l'import")}
          busy={busy === "delete"}
          onConfirm={() => void abandon()}
          onCancel={() => setConfirming(false)}
        >
          {t("Le fichier reçu et sa vérification sont supprimés ; aucun dossier n'est créé.")}
        </ConfirmDialog>
      )}
    </Presence>
    </>
  );
}
